import { focusOnFirst, trapFocus } from './focus.js';
import h from './hyperscript.js';
import readdScripts from './readdScripts.js';
export default class ModalShow {
  constructor(selector = '.show-modal', options = {}) {
    this.selector = selector;
    this.options = options = Object.assign({}, options);

    // Keep track of modals created by this system.
    let modalsData = [];
    if (selector) {
      // Listen to all click events and open a modal if it is a valid link
      document.body.addEventListener('click', function (event) {
        if (this.createFromLink(event.target)) {
          event.preventDefault();
        }
      }.bind(this), {
        capture: true
      });
    }

    /**
     * Close a modal.
     * @param {Object | HTMLElement | null} identifier The data returned on creation, the container element of the modal, or nothing at all. If nothing is provided the most recent modal is closed.
     * @returns Whether the modal was successfully closed.
     */
    this.close = function (identifier = null) {
      // Check if we have modals we can close.
      if (modalsData.length === 0) {
        return false;
      }

      // Get the modals data.
      let index = -1,
        data;
      if (identifier) {
        if (identifier instanceof HTMLElement) {
          for (let i = modalsData.length - 1; i >= 0; i--) {
            const modalItem = modalsData[i];
            if (modalItem.element === identifier) {
              index = i;
              data = modalItem;
              // If match stop searching!
              break;
            }
          }
        } else {
          index = modalsData.indexOf(identifier);
          if (index >= 0) {
            data = identifier;
          }
        }
      } else {
        // As fallback grab the last one
        index = modalsData.length - 1;
        data = modalsData[index];
      }

      // Exit early if no modal data found.
      if (!data) {
        return false;
      }

      // Destroy modal.
      if (!ModalShow.destroy(data)) {
        return false;
      }

      // Remove from list if successful.
      modalsData.splice(index, 1);
      return true;
    };
    this.create = function (content, options = {}) {
      // Create and store modal.
      const data = ModalShow.create(content, options);
      if (data) {
        modalsData.push(data);
      }
      return data;
    };
    this.createFromElement = function (element, options) {
      if (!element) {
        console.error('Unable to create modal from element, no element provided.');
        return;
      }

      // Remove script elements so code isn't ran twice.
      element.querySelectorAll('script').forEach(scriptElement => scriptElement.remove());

      // Keep track of the element's position if it is part of a DOM structure.
      let marker = null;
      if (element.parentElement) {
        // Place marker next to element.
        marker = document.createElement('template');
        element.parentElement.insertBefore(marker, element);
        // Disconnect element from DOM.
        element.parentElement.removeChild(element);
      }

      // Create modal at body.
      const data = ModalShow.create(element, Object.assign({}, options, {
        onClosing: function (data) {
          // Disconnect element from DOM.
          element.parentElement.removeChild(element);
          if (marker) {
            // Move the content back the marker.
            marker.parentElement.insertBefore(element, marker);
            // Remove marker again.
            marker.remove();
          }

          // Call before closed callback.
          if (options && options.onClosing && typeof options.onClosing === 'function') {
            options.onClosing(data);
          }
        }
      }));
      if (data) {
        // Mark as not created by show since the close handler manually cleans it up.
        data.created = false;
        modalsData.push(data);
      }
      return data;
    };
    this.createFromLink = function (element) {
      if (!element) {
        console.error('Unable to create modal from link, no element provided.');
        return;
      }

      // Check if it matches the selector.
      var link = element.closest(selector);
      if (!link || link === '') {
        return false;
      }
      let href = link.getAttribute('href');
      if (!href) {
        href = link.getAttribute('data-target');
      }
      return this.show(href);
    };
    this.show = function (selector) {
      const modal = document.querySelector(selector);
      if (!modal) {
        console.error('Unable to find element with selector "' + selector + '" to open as modal!');
        return false;
      }

      // Get modal content.
      const modalContent = modal.querySelector('.modal');
      if (!modalContent) {
        console.error('Unable to find modal in selected modal element with selector "' + selector + '"!');
        return false;
      }
      modalContent.classList.add('show');
      modalContent.style.display = 'block';
      modalContent.dispatchEvent(new CustomEvent('modal-show'));
      modalContent.dispatchEvent(new CustomEvent('modal-shown'));

      // Show backdrop.
      const modalBackdrop = modal.querySelector('.modal-backdrop');
      if (modalBackdrop) {
        modalBackdrop.classList.add('show');
      }

      // Add modal open to body.
      document.body.classList.add('modal-open');

      // Store modal data.
      const data = {
        created: false,
        element: modal,
        focusTrap: null
      };
      modalsData.push(data);

      // Close on dismiss buttons click.
      const closeCallback = () => {
        this.close(data);
      };
      const modalCloseButtons = modalContent.querySelectorAll('[data-dismiss="modal"]');
      modalCloseButtons.forEach(modalCloseButton => modalCloseButton.addEventListener('click', closeCallback));
      data.focusTrap = trapFocus(modal);
      focusOnFirst(modal.querySelector('.modal-body'));
      return data;
    };
  }
}
ModalShow.destroy = function (data) {
  // Check if element is specified.
  if (!data.element) {
    return false;
  }
  if (data.focusTrap) {
    data.focusTrap();
  }

  // Call before closed callback.
  if (data.options && data.options.onClosing && typeof data.options.onClosing === 'function') {
    data.options.onClosing(data);
  }
  let closed = false;
  if (data.created) {
    // Remove container.
    data.element.remove();
    delete data.element;
    closed = true;
  } else {
    // Hide modal content.
    const modalContent = data.element.querySelector('.modal.show');
    if (modalContent) {
      modalContent.classList.remove('show');
      modalContent.style.display = null;
      closed = true;
      modalContent.dispatchEvent(new CustomEvent('modal-hidden'));
    }
    // Hide modal backdrop.
    const modalBackdrop = data.element.querySelector('.modal-backdrop');
    if (modalBackdrop) {
      modalBackdrop.classList.remove('show');
    }
  }
  if (closed) {
    // If no modals are open then remove open class on body.
    if (!document.body.querySelector('.modal.show')) {
      document.body.classList.remove('modal-open');
    }

    // Call closed callback.
    if (data.options && data.options.onClose && typeof data.options.onClose === 'function') {
      data.options.onClose(data);
    }
  }
  return closed;
};
ModalShow.create = function (content, options = {}) {
  // Override default with given options.
  options = Object.assign({
    class: '',
    size: null,
    title: '',
    onClosing: null,
    onClose: null,
    onLoad: null
  }, options);
  let data = {
    created: true,
    element: null,
    options: options,
    focusTrap: null
  };
  const modalClose = h('button', {
    'aria-label': options.textClose,
    class: 'close',
    // Remove modal from document on click.
    click: function () {
      ModalShow.destroy(data);
    },
    'data-dismiss': 'modal'
  }, [h('span', {
    'aria-hidden': 'true'
  }, [h('i', {
    class: 'fas fa-times fa-fw'
  })])]);
  const modalBody = h('div', {
    class: 'modal-body'
  }, Array.isArray(content) ? content : [content]);
  const modalContent = h('div', {
    class: 'modal show ' + (options.class ? options.class : ''),
    'data-backdrop': 'true',
    role: 'dialog',
    style: 'display: block',
    tabindex: '-1',
    title: options.title
  }, [h('div', {
    class: 'modal-dialog modal-dialog-centered ' + (options.size ? options.size : ''),
    role: 'document'
  }, [h('div', {
    'aria-live': 'polite',
    class: 'modal-content',
    role: 'status'
  }, [h('div', {
    class: 'modal-header'
  }, [
  // Set title.
  h('div', {
    class: 'modal-title',
    role: 'heading',
    'aria-level': 2
  }, options.title),
  // Add close button.
  modalClose]), modalBody])])]);

  // Create modal.
  data.element = h('div', {}, [h('div', {
    class: 'modal-backdrop show ' + (options.class ? options.class : '')
  }), modalContent]);

  // Add modal to body.
  document.body.append(data.element);
  document.body.classList.add('modal-open');

  // Readd scripts to the DOM so they will run.
  readdScripts(modalBody, data.element);

  // Setup Popover.
  const popoverElements = $(modalBody).find('[data-toggle="popover"]');
  if (popoverElements.popover) {
    popoverElements.popover({
      container: 'body'
    });
  }
  // Setup Tooltips.
  const selectorTooltip = '[data-toggle="tooltip"]';
  const tooltipElements = $(modalBody).find(selectorTooltip);
  if (tooltipElements.tooltip) {
    tooltipElements.tooltip({
      container: 'body'
    });
  }
  const tooltipButtons = Array.from(modalBody.querySelectorAll(selectorTooltip));
  const setupTooltipButton = button => {
    button.addEventListener('click', () => {
      const tooltipElements = $(button);
      if (tooltipElements.tooltip) {
        tooltipElements.tooltip('hide');
      }
    });
  };
  for (let i = 0; i < tooltipButtons.length; i++) {
    setupTooltipButton(tooltipButtons[i]);
  }
  data.focusTrap = trapFocus(data.element);
  focusOnFirst(data.element.querySelector('.modal-body'));

  // Call loaded callback.
  if (options.onLoad && typeof options.onLoad === 'function') {
    options.onLoad();
  }
  modalContent.dispatchEvent(new CustomEvent('modal-show'));
  modalContent.dispatchEvent(new CustomEvent('modal-shown'));

  // Return data.
  return data;
};